import type { SpaceWithAvailability } from '@store/models';

/**
 * Determines whether a space is currently bookable for the requested window.
 *
 * Replaces the legacy `space.isAvailable` flag that used to be synthesized on
 * the frontend (it was never sent by the backend list response). The single
 * source of truth is now `spaceAvailability`:
 *
 *   • If `spaceAvailability` is missing or empty, availability is unknown →
 *     treat the space as bookable (existing UI behavior — the green
 *     "Available" badge and an enabled select button).
 *   • Otherwise, a space is bookable iff at least one day is available.
 *
 * Note: this differs from the previous synthesis (`every(day => available)`),
 * which incorrectly collapsed *partially* available spaces to "Booked". The
 * `SpaceStatusBadge` already renders "Available for N days" for partial
 * availability, so callers should rely on this helper.
 */
export function isSpaceBookable(space: Pick<SpaceWithAvailability, 'spaceAvailability'>): boolean {
  const days = space.spaceAvailability;
  if (!Array.isArray(days) || days.length === 0) return true;
  return days.some((d) => d.available);
}
